import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { CampaignItemDto } from '@api/Api';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import { DEMO_TOUR_APPROVAL_WORKFLOW_ID } from '@config/constants';

export default function useApprovalCampaignsTour({
  isLoading,
  campaignsList
}: {
  isLoading: boolean;
  campaignsList: {
    node: CampaignItemDto;
    cursor: string;
  }[];
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { onStartTour } = useOnboardingProcess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const isDemoOrganization = useIsDemoOrganization();

  const selectedCampaign = useMemo(() => {
    return campaignsList.find((i) => i.node.demo);
  }, [campaignsList]);

  const startTourQueryParam = searchParams.get('startTour') ?? undefined;

  const onStartTourRouteForApproval = useCallback(() => {
    onStartTour({
      type: 'route-for-approval',
      onClickNext: (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (!selectedCampaign?.node?.id) return;
        if (currentStep === 0) {
          navigate(`/campaigns/single/${selectedCampaign.node.id}`);
          return;
        }
        if (currentStep === 1) {
          dispatch(
            toggleApprovalModal({
              visible: true,
              approvalWorkflowId: DEMO_TOUR_APPROVAL_WORKFLOW_ID,
              defaultAwMode: 'create',
              permissions: {
                create: true
              },
              campaignId: selectedCampaign.node.id
            })
          );
        }
      },
      onClickPrev: (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 1) {
          navigate(`/campaigns/all/${currentWorkspace?.id}`);
        }
        // next steps in useApprovalModalTour
      }
    });
  }, [onStartTour, searchParams, selectedCampaign?.node?.id, setSearchParams]);

  useEffect(() => {
    if (isLoading) return;
    if (!isDemoOrganization) return;
    if (startTourQueryParam !== 'route-for-approval') return;
    const timeoutId = setTimeout(() => {
      onStartTourRouteForApproval();
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    startTourQueryParam,
    isDemoOrganization,
    isLoading,
    onStartTourRouteForApproval
  ]);

  return null;
}
